
  // In EditablePage.js
import React, { useState, useEffect } from 'react';
import '../css/EditableWidget.css';

const EditablePage = ({ 
  pageType,
  content, 
  onSave,
  language,
  isAuthenticated = false
}) => {
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState({
    title: '',
    titleEn: '',
    bodyHtml: '',
    bodyHtmlEn: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [sessionValid, setSessionValid] = useState(true);

  // Check session validity
  useEffect(() => {
    const checkSession = () => {
      const token = localStorage.getItem('token');
      const loginTimestamp = localStorage.getItem('loginTimestamp');
      
      if (!token || !loginTimestamp) {
        setSessionValid(false);
        return;
      }
      
      const expiryTime = parseInt(loginTimestamp) + (60 * 60 * 1000); // 1 hour expiry
      const currentTime = Date.now();
      const remainingTime = expiryTime - currentTime;
      
      setSessionValid(remainingTime > 0);
    };
    
    // Check immediately
    checkSession();
    
    // Then check every 30 seconds when editing
    let interval;
    if (editing) {
      interval = setInterval(checkSession, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [editing]);

  // Update local content when parent content changes
  useEffect(() => {
    if (content) {
      setEditedContent({
        title: content.title || '',
        titleEn: content.titleEn || '',
        bodyHtml: content.bodyHtml || '',
        bodyHtmlEn: content.bodyHtmlEn || ''
      });
    }
  }, [content]);

  const handleEditClick = () => {
    if (!isAuthenticated) return;
    
    // Check session validity before allowing edit
    const token = localStorage.getItem('token');
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    
    if (!token || !loginTimestamp) {
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    const expiryTime = parseInt(loginTimestamp) + (60 * 60 * 1000); // 1 hour expiry
    const currentTime = Date.now();
    
    if (currentTime > expiryTime) {
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    setEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(false);
    // Reset to original content
    if (content) {
      setEditedContent({
        title: content.title || '',
        titleEn: content.titleEn || '',
        bodyHtml: content.bodyHtml || '',
        bodyHtmlEn: content.bodyHtmlEn || ''
      });
    }
  };

  const handleSaveChanges = () => {
    // Check session validity before saving
    if (!sessionValid) {
      alert('Your session has expired. Please log in again and try saving again.');
      window.location.href = '/login';
      return;
    }
    
    // Save the simplified content
    const updatedContent = {
      ...editedContent,
      widgetType: pageType
    };
    onSave(updatedContent);
    setShowModal(false);
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditedContent({
      ...editedContent,
      [field]: value
    });
  };

  return (
    <div className="editable-widget editable-page">
      {/* Edit button - only visible to authenticated users */}
      {isAuthenticated && (
        <button className="edit-button" onClick={handleEditClick}>
          <span className="icon">✏️</span> 
        </button>
      )}
      
      {/* Modal for editing */}
      {showModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="edit-modal-header">
              <h3>Edit Page Content</h3>
              <button className="close-modal-button" onClick={handleCloseModal}>&times;</button>
            </div>
            
            <div className="edit-modal-content">
              <div className="edit-instructions">
                <p>Fill out both Dutch and English content for bilingual support.</p>
                <p>You can use HTML for formatting in the body content.</p>
              </div>
              
              {/* Title fields */}
              <div className="edit-item">
                <div className="edit-item-header">
                  <span>Page Title</span>
                </div>
                <div className="edit-field">
                  <label>Dutch Title:</label>
                  <input
                    type="text"
                    value={editedContent.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter title in Dutch"
                  />
                </div>
                <div className="edit-field">
                  <label>English Title:</label>
                  <input
                    type="text"
                    value={editedContent.titleEn || ''}
                    onChange={(e) => handleInputChange('titleEn', e.target.value)}
                    placeholder="Enter title in English"
                  />
                </div>
              </div>
              
              {/* HTML Body fields */}
              <div className="edit-item">
                <div className="edit-item-header">
                  <span>Page Content</span>
                </div>
                <div className="edit-field">
                  <label>Dutch Content (HTML):</label>
                  <textarea
                    value={editedContent.bodyHtml || ''}
                    onChange={(e) => handleInputChange('bodyHtml', e.target.value)}
                    placeholder="Enter page content in Dutch (HTML allowed)"
                    rows={15}
                  />
                </div>
                <div className="edit-field">
                  <label>English Content (HTML):</label>
                  <textarea
                    value={editedContent.bodyHtmlEn || ''}
                    onChange={(e) => handleInputChange('bodyHtmlEn', e.target.value)}
                    placeholder="Enter page content in English (HTML allowed)"
                    rows={15}
                  />
                </div>
              </div>
            </div>
            
            <div className="edit-modal-footer">
              <button className="cancel-button" onClick={handleCloseModal}>Cancel</button>
              <button className="save-button" onClick={handleSaveChanges}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditablePage;